import { ScatterplotLayer } from '@deck.gl/layers';
import maplibregl from 'maplibre-gl';
import type { Scene, PlotData, MetaData, MapAdapter } from '../types';
import { areaColor, PALETTE } from '../palette';
import { hexToRgba } from '../colorUtils';

interface PlotDatum {
  position: [number, number];
  color: [number, number, number, number];
  radius: number;
  plot: PlotData;
}

export class Scene1 implements Scene {
  readonly panel: HTMLElement;
  private layer: ScatterplotLayer<PlotDatum> | null = null;
  private popup: maplibregl.Popup | null = null;

  constructor(
    root: HTMLElement,
    private readonly plots: PlotData[],
    private readonly meta: MetaData,
    private readonly map: MapAdapter,
  ) {
    this.panel = document.createElement('div');
    this.panel.className = 'sm-panel';
    this.panel.dataset['scene'] = '1';
    this.panel.innerHTML = `
      <div class="sm-panel-header">
        <div class="sm-panel-title">Species Distribution</div>
        <div class="sm-panel-sub">Plot-level occurrence · ${meta.crs}</div>
      </div>
      <div class="sm-panel-narrative">
        130 survey plots establish the species occurrence baseline across three
        protected areas. Each plot is the ground truth against which
        model-detected change will be measured. Circle size reflects species
        richness per plot.
      </div>
      <div class="sm-headline">
        130 survey plots establish the species baseline — the ground truth
        against which model-detected change will be measured.
      </div>
      <div id="sm-s1-stats"></div>
      <hr class="sm-divider" style="margin-top:0;">
      <div style="padding:0 22px 6px;">
        <div class="sm-section-label" style="margin-top:0;">Survey areas</div>
        ${([ ['Chewore North', PALETTE.navy],
              ['Sapi',          PALETTE.green],
              ['Kapiri',        PALETTE.gold],
              ['Coord flagged', PALETTE.red],
           ] as [string, string][]).map(([label, col]) => `
          <div class="sm-legend-row">
            <div class="sm-legend-dot" style="background:${col};box-shadow:0 0 5px ${col};"></div>
            ${label}
          </div>`).join('')}
        <div style="font-size:9px;color:var(--c-grey);margin-top:6px;padding-bottom:8px;">
          Circle size ∝ unique species per plot
        </div>
      </div>
      <div id="sm-s1-flag" class="sm-flag-note" style="display:none;"></div>`;
    root.appendChild(this.panel);
  }

  onEnter(): void {
    this.buildStats();
    this.buildLayer();
    this.map.addLayer(this.layer!);
  }

  onLeave(): void {
    if (this.layer) this.map.removeLayer(this.layer);
    this.popup?.remove();
    this.popup = null;
  }

  private buildStats(): void {
    const el = this.panel.querySelector<HTMLElement>('#sm-s1-stats')!;
    if (el.children.length) return;

    const rows: [string, string | number][] = [
      ['Plots',        this.meta.n_plots],
      ['Occurrences',  this.meta.total_occ],
      ['Species',      this.meta.n_species],
      ...Object.entries(this.meta.area_counts).map(([k, v]) => [k, v] as [string, number]),
    ];
    el.innerHTML = rows.map(([k, v]) => `
      <div class="sm-stat-row">
        <span class="sm-stat-k">${k}</span>
        <span class="sm-stat-v">${v}</span>
      </div>`).join('');

    const flagEl = this.panel.querySelector<HTMLElement>('#sm-s1-flag')!;
    if (this.meta.n_flagged > 0) {
      flagEl.style.display = '';
      flagEl.textContent =
        `⚠ ${this.meta.n_flagged} plot${this.meta.n_flagged > 1 ? 's' : ''} ` +
        `with suspect coordinates — verify GPS`;
    }
  }

  private buildLayer(): void {
    if (this.layer) return;

    const maxSp = Math.max(...this.plots.map(p => p.n_sp));
    const data: PlotDatum[] = this.plots.map(p => ({
      position: [p.lon, p.lat] as [number, number],
      color: hexToRgba(p.flag !== '' ? PALETTE.red : areaColor(p.area)),
      radius: 5 + (p.n_sp / maxSp) * 10,
      plot: p,
    }));

    const mlMap = (this.map as unknown as { map: maplibregl.Map }).map;

    this.layer = new ScatterplotLayer<PlotDatum>({
      id: 'scene1-plots',
      data,
      getPosition: d => d.position,
      getFillColor: d => d.color,
      getRadius: d => d.radius,
      radiusUnits: 'pixels',
      stroked: true,
      getLineColor: [0, 0, 0, 80],
      lineWidthMinPixels: 1,
      pickable: true,
      opacity: 0.85,
      onClick: info => {
        if (!info.object) return true;
        const p = info.object.plot;
        const spList = p.sp.slice(0, 6).map(s => `<i>${s}</i>`).join('<br>');
        const more   = p.sp.length > 6 ? `<br>… +${p.sp.length - 6} more` : '';
        const flagged = p.flag !== '';
        this.popup?.remove();
        this.popup = new maplibregl.Popup({ maxWidth: '240px', className: 'sm-ml-popup' })
          .setLngLat([p.lon, p.lat])
          .setHTML(`
            <div class="sm-popup-title">${p.area} · Plot ${p.plot}</div>
            <div class="sm-popup-meta">${p.dim_w}×${p.dim_h} m &nbsp;·&nbsp;
              <span style="color:var(--c-gold)">${p.n_sp} species</span></div>
            <hr class="sm-popup-divider">
            <div class="sm-popup-sp">${spList}${more}</div>
            ${flagged ? `<div class="sm-popup-flag">⚠ ${p.flag}</div>` : ''}`)
          .addTo(mlMap);
        return true;
      },
    });
  }
}
